import React, { useEffect, useState, useCallback } from "react";
import { Activity, Skull, Dna, TrendingUp, Award, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STRAT_COLOR = {
  DCA: "#3b82f6",
  MEAN_REV: "#10b981",
  MOMENTUM: "#f59e0b",
};

function fmtAge(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 60) return `${min}m`;
  if (min < 1440) return `${Math.floor(min/60)}h`;
  return `${Math.floor(min/1440)}d`;
}

function CandidateCard({ c, rank }) {
  const g = c.genome;
  const isWin = c.pnl_pct >= 0;
  const sCol = STRAT_COLOR[g.strategy] || "#6b7280";
  return (
    <div style={{
      background: "#13161f",
      border: c.real_money_ready ? "2px solid #22c55e" :
              c.tier === "champion" ? "2px solid #facc15" :
              c.tier === "tiger_seed" ? "1px solid #f97316" :
              `1px solid ${rank <= 3 ? "rgba(34,197,94,0.4)" : "#2a2d39"}`,
      borderRadius: 10,
      padding: 12,
      position: "relative",
    }}>
      {rank <= 3 && (
        <div style={{
          position: "absolute", top: 8, right: 8,
          background: rank === 1 ? "#facc15" : rank === 2 ? "#94a3b8" : "#cd7f32",
          color: "#000", fontSize: 9, fontWeight: 700,
          padding: "2px 6px", borderRadius: 4,
        }}>#{rank}</div>
      )}
      {c.tier === "champion" && (
        <div style={{
          position: "absolute", top: 30, right: 8,
          background: "#facc15", color: "#000",
          fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
        }}>🏆 CHAMP</div>
      )}
      {c.tier === "tiger_seed" && (
        <div style={{
          position: "absolute", top: 30, right: 8,
          background: "#f97316", color: "#fff",
          fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
        }}>🐅 TIGER</div>
      )}
      {c.tier === "hall_of_fame" && (
        <div style={{
          position: "absolute", top: 30, right: 8,
          background: "#a855f7", color: "#fff",
          fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
        }}>⭐ HOF</div>
      )}
      {c.real_money_ready && (
        <div style={{
          position: "absolute", top: 52, right: 8,
          background: "#22c55e", color: "#000",
          fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
        }}>💰 €{c.suggested_real_eur}</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{g.symbol.replace("USDT","")}</div>
        <div style={{
          fontSize: 9, padding: "2px 5px", borderRadius: 3,
          background: sCol + "33", color: sCol, fontWeight: 600,
        }}>{g.strategy}</div>
        <div style={{ fontSize: 8, color: "#666" }}>gen {c.generation}</div>
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: isWin ? "#22c55e" : "#ef4444", display: "flex", alignItems: "center", gap: 4 }}>
        {c.pnl_pct >= 0 ? "+" : ""}{c.pnl_pct.toFixed(2)}%
        <span style={{ fontSize: 10, color: "#888", marginLeft: 8 }}>score {c.score.toFixed(1)}</span>
      </div>
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 4 }}>
        ${c.equity.toFixed(2)} • {c.deals_completed} deals ({c.deals_won}W/{c.deals_lost}L)
      </div>
      <div style={{
        marginTop: 8, padding: "5px 8px", background: "#0a0c14", borderRadius: 5,
        fontSize: 9, color: "#888", fontFamily: "monospace",
      }}>
        TP={g.tp_pct}% • SO_step={g.so_step_pct}% • mso={g.max_so} • vol×{g.vol_scale} • bo={g.bo_pct}% • trail={g.trail_pct}%
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 9, color: "#666" }}>
        <span>{c.in_deal ? `🔵 in deal · SO ${c.safety_orders_filled}/${g.max_so}` : "💤 waiting"}</span>
        <span>age {fmtAge(c.created_at)}</span>
      </div>
      {c.parent_id && (
        <div style={{ fontSize: 8, color: "#555", marginTop: 4 }}>
          ↳ mutated from {c.parent_id} (parent score {c.parent_score?.toFixed(1) || "?"})
        </div>
      )}
    </div>
  );
}

export default function LabEngineTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("alive");

  const fetchData = useCallback(async () => {
    if (!token) {
      setError("Not logged in");
      setLoading(false);
      return;
    }
    try {
      const r = await fetch(`${API}/api/skynet-brain/strategy-lab`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) { setError("Session expired"); setLoading(false); return; }
      if (!r.ok) { setError(`HTTP ${r.status}`); setLoading(false); return; }
      const j = await r.json();
      if (j.error) setError(j.error);
      else { setData(j); setError(null); }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (error && !data) return <div style={{ padding: 40, color: "#ef4444", textAlign: "center" }}>{error}</div>;
  if (loading && !data) return <div style={{ padding: 40, color: "#888", textAlign: "center" }}>Loading lab…</div>;
  if (!data) return null;

  const alive = data.alive || [];
  const killed = data.killed_recent || [];
  const meta = data.meta || {};

  // Aggregate stats
  const totalEq = alive.reduce((s, c) => s + (c.equity || 0), 0);
  const totalDeals = alive.reduce((s, c) => s + (c.deals_completed || 0), 0);
  const totalWins = alive.reduce((s, c) => s + (c.deals_won || 0), 0);
  const winRate = totalDeals > 0 ? (totalWins / totalDeals * 100) : 0;
  const totalCap = alive.reduce((s, c) => s + (c.initial_capital || 0), 0);
  const totalPnL = totalEq - totalCap;
  const totalPnLPct = totalCap > 0 ? (totalPnL / totalCap * 100) : 0;

  // Strategy distribution
  const strats = { DCA: 0, MEAN_REV: 0, MOMENTUM: 0 };
  alive.forEach(c => { strats[c.genome?.strategy] = (strats[c.genome?.strategy] || 0) + 1; });
  // Symbol distribution
  const symCounts = {};
  alive.forEach(c => {
    const sym = (c.genome?.symbol || "?").replace("USDT", "");
    symCounts[sym] = (symCounts[sym] || 0) + 1;
  });

  // Compute real-money-ready stats
  const realMoneyReady = alive.filter(c => c.real_money_ready);
  const totalSuggestedEur = realMoneyReady.reduce((s, c) => s + (c.suggested_real_eur || 0), 0);
  const championsAlive = alive.filter(c => c.tier === "champion").length;
  const tigerSeeds = alive.filter(c => c.tier === "tiger_seed").length;

  return (
    <div style={{ padding: "16px 20px", color: "#eee", maxWidth: 1600, margin: "0 auto" }}>
      {/* Header + stats */}
      <div style={{
        background: "linear-gradient(135deg, #0f1218, #1a1d29)",
        border: "1px solid #2a2d39",
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <Dna size={22} style={{ color: "#a855f7" }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>Strategy Lab Engine</div>
          <div style={{ fontSize: 11, padding: "3px 8px", background: "#a855f733", color: "#a855f7", borderRadius: 4, fontWeight: 600 }}>
            EVOLUTIONARY TOURNAMENT
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, color: "#888" }}>POOL</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#3b82f6" }}>{alive.length}/20</div>
            <div style={{ fontSize: 10, color: "#666" }}>alive candidates</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888" }}>TOTAL EQUITY</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>${totalEq.toFixed(0)}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: totalPnL >= 0 ? "#22c55e" : "#ef4444" }}>
              {totalPnLPct >= 0 ? "+" : ""}{totalPnLPct.toFixed(2)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888" }}>DEALS</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{totalDeals}</div>
            <div style={{ fontSize: 11, color: "#22c55e" }}>{winRate.toFixed(0)}% win rate</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888" }}>GENERATION</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#a855f7" }}>{meta.generations_run || 0}</div>
            <div style={{ fontSize: 10, color: "#666" }}>
              next evolve: ~{meta.last_evolve ? Math.max(0, 6 - Math.floor((Date.now() - new Date(meta.last_evolve).getTime())/3600000)) : 6}h
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888" }}>STRATEGIES</div>
            <div style={{ display: "flex", gap: 6, marginTop: 4, fontSize: 10 }}>
              <span style={{ color: STRAT_COLOR.DCA }}>DCA {strats.DCA || 0}</span>
              <span style={{ color: STRAT_COLOR.MEAN_REV }}>MR {strats.MEAN_REV || 0}</span>
              <span style={{ color: STRAT_COLOR.MOMENTUM }}>MOM {strats.MOMENTUM || 0}</span>
            </div>
          </div>
        </div>

        {/* Real money decision banner */}
        {realMoneyReady.length > 0 && (
          <div style={{
            marginTop: 14, padding: 12,
            background: "linear-gradient(135deg, #22c55e22, #15803d22)",
            border: "2px solid #22c55e",
            borderRadius: 10,
          }}>
            <div style={{ fontSize: 12, color: "#22c55e", fontWeight: 700, marginBottom: 4 }}>
              💰 REAL MONEY READY: {realMoneyReady.length} candidates qualified
            </div>
            <div style={{ fontSize: 11, color: "#aaa" }}>
              Suggested total deployment: <b style={{ color: "#22c55e" }}>€{totalSuggestedEur}</b>
              {" · "}({championsAlive} Champions, {tigerSeeds} Tigers active)
            </div>
            <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
              Criteria: 10+ deals · 80%+ win rate · 72h+ alive · 2%+ P&L · max 10% DD
            </div>
          </div>
        )}

        {/* Symbol distribution */}
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {Object.entries(symCounts).sort((a,b) => b[1]-a[1]).map(([sym, n]) => (
            <span key={sym} style={{
              fontSize: 10, padding: "3px 8px", background: "#0a0c14",
              border: "1px solid #2a2d39", borderRadius: 12, color: "#aaa",
            }}>
              {sym} <span style={{ color: "#666" }}>×{n}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button onClick={() => setTab("alive")} style={{
          padding: "8px 16px", background: tab === "alive" ? "#3b82f6" : "#1a1d29",
          color: tab === "alive" ? "#fff" : "#aaa", border: "1px solid " + (tab === "alive" ? "#3b82f6" : "#2a2d39"),
          borderRadius: 6, fontSize: 12, cursor: "pointer", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <Activity size={14} /> Alive ({alive.length})
        </button>
        <button onClick={() => setTab("killed")} style={{
          padding: "8px 16px", background: tab === "killed" ? "#ef4444" : "#1a1d29",
          color: tab === "killed" ? "#fff" : "#aaa", border: "1px solid " + (tab === "killed" ? "#ef4444" : "#2a2d39"),
          borderRadius: 6, fontSize: 12, cursor: "pointer", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <Skull size={14} /> Killed ({killed.length})
        </button>
      </div>

      {/* Cards grid */}
      {tab === "alive" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {alive.map((c, i) => <CandidateCard key={c.id} c={c} rank={i+1} />)}
        </div>
      )}

      {tab === "killed" && (
        <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#0a0c14", color: "#666" }}>
                <th style={{ padding: 10, textAlign: "left" }}>ID</th>
                <th style={{ padding: 10, textAlign: "left" }}>Symbol</th>
                <th style={{ padding: 10, textAlign: "left" }}>Strategy</th>
                <th style={{ padding: 10, textAlign: "right" }}>Final Score</th>
                <th style={{ padding: 10, textAlign: "right" }}>Final Equity</th>
                <th style={{ padding: 10, textAlign: "right" }}>Deals</th>
                <th style={{ padding: 10, textAlign: "left" }}>Killed</th>
              </tr>
            </thead>
            <tbody>
              {killed.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: 30, textAlign: "center", color: "#666" }}>No kills yet — first evolution in ~6h</td></tr>
              ) : killed.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid #1a1d29" }}>
                  <td style={{ padding: 10, color: "#888", fontFamily: "monospace" }}>{c.id}</td>
                  <td style={{ padding: 10, color: "#fff" }}>{c.genome?.symbol?.replace("USDT","")}</td>
                  <td style={{ padding: 10, color: STRAT_COLOR[c.genome?.strategy] }}>{c.genome?.strategy}</td>
                  <td style={{ padding: 10, color: "#ef4444", textAlign: "right", fontWeight: 600 }}>
                    {c.final_score?.toFixed(1) || "—"}
                  </td>
                  <td style={{ padding: 10, color: "#aaa", textAlign: "right" }}>${c.equity?.toFixed(2)}</td>
                  <td style={{ padding: 10, color: "#888", textAlign: "right" }}>{c.deals_completed || 0}</td>
                  <td style={{ padding: 10, color: "#666" }}>{fmtAge(c.killed_at)} ago</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 24, padding: 12, fontSize: 10, color: "#555", textAlign: "center" }}>
        Auto-refresh 30s • Tick every 5min via cron • Evolution every 6h
      </div>
    </div>
  );
}
